import React from 'react';
import classNames from 'classnames';
import { REACT_APP_API_BASE } from '../../../../config';
import { renderToString } from 'react-dom/server';
import SpinnerDots from '../../../spinner/dots';
import '../../styles/components/popup.scss';
import * as districtCache from '../districts/district-cache';
import * as unitsOps from '../units/units-operations';
import * as mapOps from '../map/map-operations';
import { calculateClassificationData } from '../districts/district-data-calculator';
import { applyFilters as applyFiltersToUnits } from '../filters/filter-applicator';

function getDistrictsForUnit(companyId) {
    if (!companyId) return [];
    const all = (districtCache.getAllDistrictsForScenario && districtCache.getAllDistrictsForScenario()) || [];
    const companyIdStr = String(companyId);
    return all.filter(d =>
        d &&
        Array.isArray(d.units) &&
        d.units.some(id => String(id) === companyIdStr)
    );
}

async function removeUnitFromDistrict(districtId, unitMeta, fetchFn) {
    const district = districtCache.getDistrictById
        ? districtCache.getDistrictById(districtId)
        : null;
    if (!district) return;

    const filters = district.salesDistrictFilters || {};
    const list = Array.isArray(filters.unitsIncludeExclude)
        ? [...filters.unitsIncludeExclude]
        : [];

    // Säkerställ att vi inte kan få både include:true och include:false för samma delfiId.
    const unitIdStr = unitMeta?.delfiId != null ? String(unitMeta.delfiId) : null;
    const hadIncludedEntry = unitIdStr
        ? list.some(item => item && String(item.delfiId) === unitIdStr && item.include === true)
        : false;
    const nextList = unitIdStr
        ? list.filter(item => item && String(item.delfiId) !== unitIdStr)
        : list;

    // Om den redan var explicit inkluderad i filtret ska den bara tas bort ur listan.
    // Endast när den INTE var inkluderad markerar vi den som explicit exkluderad.
    if (!hadIncludedEntry) {
        nextList.push({
            delfiId: unitMeta.delfiId,
            name: unitMeta.name || '',
            include: false
        });
    }

    const updatedFilters = {
        ...filters,
        unitsIncludeExclude: nextList
    };

    // 1) Uppdatera bara filter för target-distriktet först.
    districtCache.set(districtId, {
        ...district,
        salesDistrictFilters: updatedFilters,
        isDirty: true
    });

    // 2) Räkna om metrics ENDAST för target-distriktet.
    // Målet är att bara den borttagna enheten får default-färg (inte att hela distrikt försvinner).
    const updatedDistrict = districtCache.getDistrictById
        ? districtCache.getDistrictById(districtId)
        : null;
    if (!updatedDistrict) return;

    if (!unitMeta || unitMeta.delfiId == null) return;

    // Hämta companyId så vi kan uppdatera `district.units` (som styr färglogiken).
    const companyIdStr = unitMeta.companyId != null
        ? String(unitMeta.companyId)
        : (() => {
            const allUnits = Array.isArray(updatedDistrict.allUnits) ? updatedDistrict.allUnits : [];
            const found = allUnits.find(
                u => u && u.delfiId != null && String(u.delfiId) === String(unitMeta.delfiId)
            );
            return found && found.companyId != null ? String(found.companyId) : null;
        })();
    if (!companyIdStr) return;

    const baseUnits = Array.isArray(updatedDistrict.units) ? updatedDistrict.units : [];
    const unitsNew = baseUnits.filter(cid => String(cid) !== companyIdStr);
    const unitsNewSet = new Set(unitsNew.map(cid => String(cid)));

    const allUnits = Array.isArray(updatedDistrict.allUnits) ? updatedDistrict.allUnits : [];
    const unitsNewObjects = allUnits.filter(
        u => u && u.companyId != null && unitsNewSet.has(String(u.companyId))
    );

    const numberOfHits = unitsNewObjects.length;
    const turnOver = unitsNewObjects.reduce((sum, u) => sum + (u.turnover || 0), 0);
    const salesArea = unitsNewObjects.reduce((sum, u) => sum + (u.retailArea || 0), 0);

    const scenarioId = updatedDistrict.salesDistrictScenarioId;
    const classifications = districtCache.getClassifications
        ? districtCache.getClassifications(scenarioId)
        : [];

    const frequencyData = calculateClassificationData(
        classifications,
        unitsNewObjects,
        updatedDistrict.salesDistrictType ? updatedDistrict.salesDistrictType.toLowerCase() : '',
        updatedDistrict.allUnits
    );

    // `filteredUnits` används av units-operations för att bestämma *vilka pins som ska synas*
    // i ALL/AREA. Den ska därför beräknas via filterkedjan, inte via `district.units` (område-medlemskap).
    const filteredUnitsForCache = applyFiltersToUnits(
        allUnits,
        updatedFilters,
        updatedDistrict.salesDistrictType ? updatedDistrict.salesDistrictType.toLowerCase() : ''
    );

    districtCache.set(districtId, {
        ...updatedDistrict,
        salesDistrictFilters: updatedFilters,
        // `units` styr färg (vilka companyIds som tillhör distriktet)
        units: unitsNew,
        filteredUnits: filteredUnitsForCache,
        numberOfHits,
        turnOver,
        salesArea,
        frequency: frequencyData.frequency,
        vistingTimeHours: frequencyData.vistingTimeHours,
        salesDistrictArea: updatedDistrict.salesDistrictArea || 0,
        isDirty: true
    });

    // Synca React-state (allDistricts) via externa handlers från use-district-map
    const external = mapOps.getExternalHandlers && mapOps.getExternalHandlers();
    if (external && typeof external.syncAllDistrictsFromCache === 'function') {
        external.syncAllDistrictsFromCache();
    }

    // Rendera alltid om units baserat på det aktiva distriktet,
    // eftersom färglogiken använder alla distrikt i cachen.
    const currentId = districtCache.getCurrentDistrictId
        ? districtCache.getCurrentDistrictId()
        : null;
    if (!currentId) return;

    const activeDistrict = districtCache.getDistrictById
        ? districtCache.getDistrictById(currentId)
        : null;
    if (!activeDistrict) return;

    await unitsOps.renderUnits(
        activeDistrict.filteredUnits,
        activeDistrict.selectedAreas,
        fetchFn,
        activeDistrict.salesDistrictFilters,
        activeDistrict.allUnits,
        true,
        activeDistrict.units
    );
}

async function addUnitToDistrict(districtId, unitMeta, fetchFn) {
    const district = districtCache.getDistrictById
        ? districtCache.getDistrictById(districtId)
        : null;
    if (!district) return;

    const filters = district.salesDistrictFilters || {};
    const list = Array.isArray(filters.unitsIncludeExclude)
        ? [...filters.unitsIncludeExclude]
        : [];

    // Säkerställ att vi inte kan få både include:true och include:false för samma delfiId.
    const unitIdStr = unitMeta?.delfiId != null ? String(unitMeta.delfiId) : null;
    const hadExcludedEntry = unitIdStr
        ? list.some(item => item && String(item.delfiId) === unitIdStr && item.include === false)
        : false;
    const nextList = unitIdStr
        ? list.filter(item => item && String(item.delfiId) !== unitIdStr)
        : list;

    // Om den redan var explicit exkluderad i filtret ska den bara tas bort ur listan.
    // Endast när den INTE var exkluderad markerar vi den som explicit inkluderad.
    if (!hadExcludedEntry) {
        nextList.push({
            delfiId: unitMeta.delfiId,
            name: unitMeta.name || '',
            include: true
        });
    }

    const updatedFilters = {
        ...filters,
        unitsIncludeExclude: nextList
    };

    // 1) Uppdatera bara filter för target-distriktet först.
    districtCache.set(districtId, {
        ...district,
        salesDistrictFilters: updatedFilters,
        isDirty: true
    });

    // 2) Räkna om metrics ENDAST för target-distriktet.
    const updatedDistrict = districtCache.getDistrictById
        ? districtCache.getDistrictById(districtId)
        : null;
    if (!updatedDistrict) return;

    if (!unitMeta || unitMeta.delfiId == null) return;

    const companyIdStr = unitMeta.companyId != null ? String(unitMeta.companyId) : null;
    if (!companyIdStr) return;

    const baseUnits = Array.isArray(updatedDistrict.units) ? updatedDistrict.units : [];
    const unitsNew = baseUnits.some(cid => String(cid) === companyIdStr)
        ? [...baseUnits]
        : [...baseUnits, unitMeta.companyId];

    const unitsNewSet = new Set(unitsNew.map(cid => String(cid)));

    const allUnits = Array.isArray(updatedDistrict.allUnits) ? updatedDistrict.allUnits : [];
    const unitsNewObjects = allUnits.filter(
        u => u && u.companyId != null && unitsNewSet.has(String(u.companyId))
    );

    const numberOfHits = unitsNewObjects.length;
    const turnOver = unitsNewObjects.reduce((sum, u) => sum + (u.turnover || 0), 0);
    const salesArea = unitsNewObjects.reduce((sum, u) => sum + (u.retailArea || 0), 0);

    const scenarioId = updatedDistrict.salesDistrictScenarioId;
    const classifications = districtCache.getClassifications
        ? districtCache.getClassifications(scenarioId)
        : [];

    const frequencyData = calculateClassificationData(
        classifications,
        unitsNewObjects,
        updatedDistrict.salesDistrictType ? updatedDistrict.salesDistrictType.toLowerCase() : '',
        updatedDistrict.allUnits
    );

    const filteredUnitsForCache = applyFiltersToUnits(
        allUnits,
        updatedFilters,
        updatedDistrict.salesDistrictType ? updatedDistrict.salesDistrictType.toLowerCase() : ''
    );

    districtCache.set(districtId, {
        ...updatedDistrict,
        salesDistrictFilters: updatedFilters,
        // `units` styr färglogiken.
        units: unitsNew,
        filteredUnits: filteredUnitsForCache,
        numberOfHits,
        turnOver,
        salesArea,
        frequency: frequencyData.frequency,
        vistingTimeHours: frequencyData.vistingTimeHours,
        salesDistrictArea: updatedDistrict.salesDistrictArea || 0,
        isDirty: true
    });

    // Synca React-state (allDistricts) via externa handlers från use-district-map
    const external = mapOps.getExternalHandlers && mapOps.getExternalHandlers();
    if (external && typeof external.syncAllDistrictsFromCache === 'function') {
        external.syncAllDistrictsFromCache();
    }

    // Rerendera alltid om units baserat på det aktiva distriktet
    const currentId = districtCache.getCurrentDistrictId
        ? districtCache.getCurrentDistrictId()
        : null;
    if (!currentId) return;

    const activeDistrict = districtCache.getDistrictById
        ? districtCache.getDistrictById(currentId)
        : null;
    if (!activeDistrict) return;

    await unitsOps.renderUnits(
        activeDistrict.filteredUnits,
        activeDistrict.selectedAreas,
        fetchFn,
        activeDistrict.salesDistrictFilters,
        activeDistrict.allUnits,
        true,
        activeDistrict.units
    );
}

const CustomMarker = ({
    maps,
    ...props
}) => {
    const fetchUnitInfo = async (id) => {
        const res = await props.fetch(`${REACT_APP_API_BASE}/units/${id}`);
        return await res.json();
    }

    class Popup extends maps.OverlayView {
        constructor() {
            super();
            
            if (!props.units || !Array.isArray(props.units) || props.units.length === 0) {
                throw new Error('No units provided to Popup');
            }

            const unitPos = new maps.LatLng(props.units[0].latitude, props.units[0].longitude);
            this.latlng = unitPos;
            this.units = props.units;
        }

        async createDiv() {
            this.div = document.createElement("div");
            this.div.style.position = "absolute";
            this.div.style.zIndex = 10000;
            // Förhindra att klick/pointer-events “läcker” till kartan/andra lyssnare,
            // annars kan native <select>-dropdownen öppnas och direkt stängas igen.
            this.div.onclick = (e) => {
                e.stopPropagation();
            };
            this.positionDiv();
            
            this.div.innerHTML = renderToString(
                <div className="unit-popup unit-popup--is-loading">
                    <div className="unit-popup__bubble">
                        <div className={classNames({
                            'unit-popup__content': true,
                            'unit-popup__content--multiple': this.units.length > 1
                        })}>
                            <button type="button" className="unit-popup__close"></button>
                            <div className="unit-popup__content-inner">
                            </div>
                        </div>
                    </div>
                    <SpinnerDots />
                </div>
            );

            this.appendDivToOverlay();

            const elm = this.div.querySelectorAll('.unit-popup__content-inner');

            const bindRemoveButtons = (root) => {
                const removeButtons = root.querySelectorAll('.unit-popup__district-remove');
                removeButtons.forEach((btn) => {
                    btn.onclick = async (e) => {
                        e.stopPropagation();
                        const districtId = btn.getAttribute('data-district-id');
                        const delfiId = btn.getAttribute('data-delfi-id');
                        const companyId = btn.getAttribute('data-company-id');
                        const unitName = btn.getAttribute('data-unit-name') || '';
                        if (!districtId || delfiId == null) return;

                        const unitContainer = btn.closest('.unit-popup__content-unit');

                        await removeUnitFromDistrict(
                            districtId,
                            {
                                delfiId: Number(delfiId),
                                name: unitName,
                                companyId: companyId != null ? Number(companyId) : null
                            },
                            props.fetch
                        );

                        // Refresh UI state (inkl. select/disabled rules) efter dynamisk DOM-uppdatering i cache.
                        if (unitContainer && unitContainer.isConnected) {
                            await refreshUnitDistrictUI(unitContainer);
                        }
                    };
                });
            };

            const bindAddControls = (root) => {
                const addButtons = root.querySelectorAll('.unit-popup__district-add-btn');
                addButtons.forEach((btn) => {
                    const unitContainer = btn.closest('.unit-popup__content-unit');
                    if (!unitContainer) return;

                    const select = unitContainer.querySelector('.unit-popup__district-add-select');
                    if (!select) return;

                    // Krav: knappen ska vara disabled tills ett distrikt är valt.
                    const syncAddBtnState = () => {
                        // Tom option => disabled enligt krav
                        btn.disabled = !select.value;
                    };
                    select.onchange = syncAddBtnState;
                    select.addEventListener('change', syncAddBtnState);
                    select.addEventListener('blur', syncAddBtnState);
                    syncAddBtnState();

                    // Låt native select-dropdown stanna öppen genom att stoppa bubbla event.
                    select.addEventListener('click', (e) => e.stopPropagation());
                    select.addEventListener('mousedown', (e) => e.stopPropagation(), true);
                    select.addEventListener('pointerdown', (e) => e.stopPropagation(), true);

                    btn.onclick = async (e) => {
                        e.stopPropagation();
                        if (!select.value) return;

                        btn.disabled = true;

                        const unitDelfiId = unitContainer.getAttribute('data-unit-delfi-id');
                        const unitCompanyId = unitContainer.getAttribute('data-unit-company-id');
                        const unitName = unitContainer.getAttribute('data-unit-name') || '';
                        const targetDistrictId = select.value;

                        await addUnitToDistrict(
                            targetDistrictId,
                            {
                                delfiId: Number(unitDelfiId),
                                name: unitName,
                                companyId: unitCompanyId != null ? Number(unitCompanyId) : null
                            },
                            props.fetch
                        );

                        if (unitContainer && unitContainer.isConnected) {
                            await refreshUnitDistrictUI(unitContainer);
                        }
                    };
                });
            };

            const refreshUnitDistrictUI = async (unitContainer) => {
                const unitDelfiId = unitContainer.getAttribute('data-unit-delfi-id');
                const unitCompanyId = unitContainer.getAttribute('data-unit-company-id');

                if (!unitCompanyId) return;

                const unitDistricts = getDistrictsForUnit(unitCompanyId);
                const districtsBlock = unitContainer.querySelector('.unit-popup__districts');
                const districtAddBlock = unitContainer.querySelector('.unit-popup__district-add');

                // 1) Uppdatera "Säljdistrikt"-listan
                if (unitDistricts.length === 0) {
                    if (districtsBlock) districtsBlock.remove();
                } else {
                    if (!districtsBlock) {
                        const newBlock = document.createElement('div');
                        newBlock.className = 'unit-popup__districts';
                        newBlock.setAttribute('data-unit-delfi-id', unitDelfiId);
                        newBlock.innerHTML = `<h5>Ingår i distrikt</h5><ul class="unit-popup__district-list"></ul>`;
                        districtAddBlock.insertAdjacentElement('afterend', newBlock);
                    }

                    const ul = unitContainer.querySelector('.unit-popup__district-list');
                    if (ul) {
                        ul.innerHTML = unitDistricts.map(d => {
                            return `
                                <li key="${d.id}">
                                    <span
                                        class="unit-popup__district-color"
                                        style="background-color:${d.color}"
                                    ></span>
                                    ${d.name}
                                    <button
                                        type="button"
                                        class="unit-popup__district-remove"
                                        data-district-id="${d.id}"
                                        data-delfi-id="${unitDelfiId}"
                                        data-company-id="${unitCompanyId}"
                                        data-unit-name="${unitContainer.getAttribute('data-unit-name') || ''}"
                                    >
                                        Ta bort från distrikt
                                    </button>
                                </li>
                            `;
                        }).join('');
                    }
                }

                // 2) Uppdatera select + disabled/regler
                const select = unitContainer.querySelector('.unit-popup__district-add-select');
                const addBtn = unitContainer.querySelector('.unit-popup__district-add-btn');
                if (!select || !addBtn) return;

                const allDistricts = (districtCache.getAllDistrictsForScenario && districtCache.getAllDistrictsForScenario()) || [];
                const activeDistrictId = districtCache.getCurrentDistrictId ? districtCache.getCurrentDistrictId() : null;
                const activeDistrictIdStr = activeDistrictId != null ? String(activeDistrictId) : null;

                const belongsToActive = activeDistrictIdStr
                    ? unitDistricts.some(d => String(d.id) === activeDistrictIdStr)
                    : false;

                const optionsDistricts = belongsToActive
                    ? allDistricts.filter(d => d && String(d.id) !== activeDistrictIdStr)
                    : allDistricts.filter(d => d);

                const defaultValue = belongsToActive ? '' : (activeDistrictIdStr || '');

                const placeholder = `<option value="">Välj distrikt</option>`;
                const districtOptionsHtml = optionsDistricts
                    .map(d => `<option value="${String(d.id)}">${d.name}</option>`)
                    .join('');

                select.innerHTML = `${placeholder}${districtOptionsHtml}`;
                select.value = defaultValue;
                addBtn.disabled = !select.value;

                // Rebind remove-knappar eftersom vi kan ha återskapat DOM.
                bindRemoveButtons(unitContainer);
            };

            for (let i = 0; i < this.units.length; i++) {
                const unitMeta = this.units[i];
                const unit = await fetchUnitInfo(unitMeta.delfiId);
                const unitDistricts = getDistrictsForUnit(unitMeta.companyId || unitMeta.companyId);

                const allDistricts = (districtCache.getAllDistrictsForScenario && districtCache.getAllDistrictsForScenario()) || [];
                const activeDistrictId = districtCache.getCurrentDistrictId ? districtCache.getCurrentDistrictId() : null;
                const activeDistrictIdStr = activeDistrictId != null ? String(activeDistrictId) : null;
                const unitBelongsToActive = activeDistrictIdStr
                    ? unitDistricts.some(d => String(d.id) === activeDistrictIdStr)
                    : false;

                const defaultSelectValue = unitBelongsToActive ? '' : (activeDistrictIdStr || '');
                const isAddDisabled = !defaultSelectValue;

                const availableDistrictsForSelect = unitBelongsToActive
                    ? allDistricts.filter(d => d && String(d.id) !== activeDistrictIdStr)
                    : allDistricts.filter(d => d);

                elm[0].innerHTML =
                    elm[0].innerHTML +
                    renderToString(
                        <div
                            className="unit-popup__content-unit"
                            data-unit-delfi-id={unitMeta.delfiId}
                            data-unit-company-id={unitMeta.companyId}
                            data-unit-name={unit.UnitName}
                        >
                            <h4>
                                <a href={`/unit/${unit.DelfiId}`} target="_blank" rel="noreferrer">
                                    {unit.UnitName}
                                </a>
                            </h4>
                            <p>
                                {unit.PostalAddress.Address}, {unit.PostalAddress.PostalCode}{' '}
                                {unit.PostalAddress.City}
                            </p>

                            <ul className="unit-popup__unit-info">
                                <li>
                                    Delfinr: <strong>{unit.DelfiId}</strong>
                                </li>
                                {unit.TurnoverNumeric && (
                                    <li>
                                        Omsättning:{' '}
                                        <strong>{unit.TurnoverNumeric.toLocaleString('sv')} kkr</strong>
                                    </li>
                                )}
                            </ul>

                            <div className="unit-popup__district-add">
                                <div>
                                    <label
                                        className="unit-popup__district-add-label"
                                        htmlFor={`unit-popup__district-add-select-${unitMeta.delfiId}`}
                                    >
                                        {districtCache.getCurrent()?.salesDistrictType?.toLowerCase() === 'foodservice'
                                            ? 'Lägg till enhet i distrikt'
                                            : 'Lägg till butik i distrikt'}
                                    </label>
                                </div>
                                <div className="unit-popup__district-add-select-container">
                                    <select
                                        className="unit-popup__district-add-select"
                                        id={`unit-popup__district-add-select-${unitMeta.delfiId}`}
                                        defaultValue={defaultSelectValue}
                                    >
                                        <option value="">Välj distrikt</option>
                                        {availableDistrictsForSelect.map(d => (
                                            <option key={d.id} value={String(d.id)}>{d.name}</option>
                                        ))}
                                    </select>
                                    <button
                                        type="button"
                                        className="unit-popup__district-add-btn"
                                        disabled={isAddDisabled}
                                    >
                                        + Lägg till
                                    </button>
                                </div>
                            </div>

                            {unitDistricts.length > 0 && (
                                <div className="unit-popup__districts">
                                    <h5>Ingår i distrikt</h5>
                                    <ul className="unit-popup__district-list">
                                        {unitDistricts.map(d => (
                                            <li key={d.id}>
                                                <span
                                                    className="unit-popup__district-color"
                                                    style={{ backgroundColor: d.color }}
                                                ></span>
                                                {d.name}
                                                <button
                                                    type="button"
                                                    className="unit-popup__district-remove"
                                                    data-district-id={d.id}
                                                    data-delfi-id={unitMeta.delfiId}
                                                    data-company-id={unitMeta.companyId}
                                                    data-unit-name={unit.UnitName}
                                                >Ta bort från distrikt</button>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    );
            }

            const button = this.div.querySelectorAll('.unit-popup__close')[0];
            button.onclick = (e) => {
                e.stopPropagation();
                this.remove();
            }

            bindRemoveButtons(this.div);
            bindAddControls(this.div);

            this.div.firstChild.className = 'unit-popup';
        }

        appendDivToOverlay() {
            const panes = this.getPanes();
            panes.overlayImage.style.zIndex = 200;
            panes.overlayImage.appendChild(this.div);

            maps.event.addDomListener(this.div, 'DOMMouseScroll', (e) => {
                e.stopPropagation();
            });

            maps.event.addDomListener(this.div, 'mousewheel', (e) => {
                e.stopPropagation();
            });
        }
  
        positionDiv() {
            const point = this.getProjection().fromLatLngToDivPixel(this.latlng);
            //let offset = 25;
            if (point) {
                this.div.style.left = `${point.x}px`;
                this.div.style.top = `${point.y}px`;
            }
        }
  
        async draw() {
            if (!this.div) {
                await this.createDiv();
            }

            this.positionDiv();
        }

        onRemove() {
            if (this.div) {
                this.div.parentNode.removeChild(this.div);
                delete this.div;
            }
        }

        remove() {
            if (this.getMap()) {
                this.setMap(null);
            }        
        }
    }

    return new Popup();
};

export default CustomMarker;
  